import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { DomainName } from '../types/models/DomainName';
import type { ApiResponseDomainNameSingle } from '../types/api/ApiResponseDomainName';

interface UseDomainNameReturn {
  domainName: DomainName | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useDomainName = (id: number): UseDomainNameReturn => {
  const [domainName, setDomainName] = useState<DomainName | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchDomainName = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/domain-names/${id}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseDomainNameSingle;
      if (data.status === 'success' && data.data?.domain_name) {
        setDomainName(data.data.domain_name);
      } else if (data.status === 'fail' || data.status === 'error') {
        setError(data.message ?? 'Failed to load domain name');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the domain name');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, id]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchDomainName();
    }
  }, [auth.isAuthenticated, fetchDomainName]);

  return { domainName, loading, error, refetch: fetchDomainName };
};
